import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpenIcon, TrophyIcon, FireIcon, StarIcon, ChevronRightIcon, PlayIcon, ClipboardDocumentListIcon, GlobeAltIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import Layout from '../../components/Layout';
import { CefrBadge } from '../../components/Badge';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { useTranslation } from '../../i18n/LanguageContext';
import LocationBanner from '../../components/LocationBanner';
import { StudentSessionCard } from '../../components/VideoSessionCard';
import StudentMyTests from './StudentMyTests';

const C = { teal:'#00B4D8', tealDark:'#0087A8', navy:'#0D1B2A', success:'#10B981', warning:'#F59E0B', dark:'#1F2937', grey:'#6B7280', border:'#E5E7EB', bg:'#F0F7FA' };

const LESSON_TYPES = [
  { id:'grammar', label:'Grammar', icon:'📚', color:'#8B5CF6' },
  { id:'vocabulary', label:'Vocabulary', icon:'📖', color:C.teal },
  { id:'reading', label:'Reading', icon:'📰', color:'#F59E0B' },
  { id:'writing', label:'Writing', icon:'✍️', color:'#10B981' },
  { id:'conversation', label:'Conversation', icon:'💬', color:'#EF4444' },
  { id:'ielts', label:'IELTS', icon:'🎯', color:'#3B82F6' },
  { id:'business', label:'Business', icon:'💼', color:'#6B7280' },
];

const CEFR_ORDER = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];




const GROUP_PACKAGE_LABELS = {
  online_group: 'Online Group Classes',
  physical: 'Physical Group Classes',
  starter_group: 'Starter Group',
  standard: 'Standard Group',
  premium: 'Premium Group',
  ielts_intensive: 'IELTS Group Intensive',
  corporate: 'Corporate Group',
};

function TutorPreferenceCard({ dash, sessions = [] }) {
  const [tutors, setTutors] = React.useState([]);
  const [showModal, setShowModal] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [prefTutor, setPrefTutor] = React.useState(dash?.student?.tutor_preference || null);

  const loadTutors = () => {
    api.get('/api/student/available-tutors').then(r => setTutors(r.data.tutors || [])).catch(() => {});
  };

  const setPref = async (tutorId) => {
    setSaving(true);
    try {
      await api.put('/api/student/tutor-preference', { tutor_id: tutorId });
      setPrefTutor(tutorId);
      setShowModal(false);
    } catch {
      alert('Failed to save preference');
    } finally {
      setSaving(false);
    }
  };

  const prefName = tutors.find(t => t.id === prefTutor)?.full_name || null;
  const pkg = dash?.student?.package;
  const upNext = sessions.slice(0, 5);  // show next 5 sessions
  const hasSchedule = sessions.length > 0;

  const fmtDate = (iso) => {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday:'short', day:'numeric', month:'short' });
  };

  return (
    <>
      {/* Header banner */}
      <div style={{ background:'linear-gradient(135deg, #0D1B2A 0%, #1a3560 100%)', borderRadius:hasSchedule ? '14px 14px 0 0' : 14, padding:20, marginBottom: hasSchedule ? 0 : 24, display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
        <div style={{ fontSize:36 }}>📅</div>
        <div style={{ flex:1, minWidth:200 }}>
          <div style={{ color:'#fff', fontWeight:800, fontSize:16, marginBottom:4 }}>
            {hasSchedule ? 'Your sessions are scheduled' : 'Your sessions are auto-scheduled'}
          </div>
          <div style={{ color:'rgba(255,255,255,0.7)', fontSize:13, lineHeight:1.5 }}>
            {hasSchedule
              ? `${sessions.length} sessions confirmed · ${GROUP_PACKAGE_LABELS[pkg] || 'Group classes'}`
              : `${GROUP_PACKAGE_LABELS[pkg] || 'Group classes'} — sessions will appear here once your group is assigned.`}
          </div>
          {prefTutor && prefName && (
            <div style={{ marginTop:6, fontSize:12, color:'#00B4D8' }}>⭐ Preferred tutor: <strong>{prefName}</strong></div>
          )}
        </div>
        <button
          onClick={() => { loadTutors(); setShowModal(true); }}
          style={{ background:'#00B4D8', color:'#fff', border:'none', borderRadius:10, padding:'10px 20px', fontWeight:700, fontSize:13, cursor:'pointer', whiteSpace:'nowrap' }}
        >
          {prefTutor ? '✏️ Change Tutor' : '⭐ Set Preference'}
        </button>
      </div>

      {/* Upcoming sessions list */}
      {hasSchedule && (
        <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderTop:'none', borderRadius:'0 0 14px 14px', marginBottom:24, overflow:'hidden' }}>
          {upNext.map((s, i) => {
            const isToday = s.date === new Date().toISOString().slice(0,10);
            return (
              <div key={s.booking_id} style={{
                display:'flex', alignItems:'center', gap:14, padding:'12px 20px',
                borderTop: i === 0 ? 'none' : '1px solid #F3F4F6',
                background: isToday ? '#F0FDF4' : '#fff',
              }}>
                <div style={{ width:44, textAlign:'center', flexShrink:0 }}>
                  <div style={{ fontSize:18, fontWeight:800, color: isToday ? '#10B981' : '#0D1B2A', lineHeight:1 }}>
                    {new Date(s.date + 'T00:00:00').getDate()}
                  </div>
                  <div style={{ fontSize:11, color:'#6B7280', fontWeight:600, textTransform:'uppercase' }}>
                    {new Date(s.date + 'T00:00:00').toLocaleDateString('en-GB', { month:'short' })}
                  </div>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:'#1F2937' }}>
                    {fmtDate(s.date)} · {s.time_label}
                    {isToday && <span style={{ marginLeft:8, fontSize:11, background:'#10B981', color:'#fff', padding:'2px 8px', borderRadius:10, fontWeight:600 }}>TODAY</span>}
                  </div>
                  <div style={{ fontSize:12, color:'#6B7280', marginTop:1 }}>
                    {s.tutor_name} · {s.duration_min} min · {s.delivery_mode === 'remote' ? '🖥 Online' : '🏫 In person'}
                  </div>
                </div>
                {s.is_live && s.can_join && (
                  <a href={s.video_link} target="_blank" rel="noreferrer"
                    style={{ background:'#10B981', color:'#fff', border:'none', borderRadius:8, padding:'8px 16px', fontWeight:700, fontSize:13, textDecoration:'none', whiteSpace:'nowrap' }}>
                    Join Now
                  </a>
                )}
                {isToday && !s.is_live && (
                  <span style={{ fontSize:12, color:'#6B7280', whiteSpace:'nowrap' }}>
                    in {Math.round(s.minutes_until / 60 > 1 ? s.minutes_until / 60 : s.minutes_until)} {s.minutes_until >= 60 ? 'hr' : 'min'}
                  </span>
                )}
              </div>
            );
          })}
          {sessions.length > 5 && (
            <div style={{ padding:'10px 20px', textAlign:'center', borderTop:'1px solid #F3F4F6' }}>
              <a href="/my-sessions" style={{ fontSize:13, color:'#00B4D8', fontWeight:600, textDecoration:'none' }}>
                View all {sessions.length} sessions →
              </a>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <div onClick={() => setShowModal(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:16, padding:28, width:480, maxWidth:'100%', maxHeight:'80vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin:'0 0 6px', fontSize:18, fontWeight:700, color:'#1F2937' }}>Choose Your Preferred Tutor</h3>
            <p style={{ margin:'0 0 16px', fontSize:13, color:'#6B7280' }}>This is a preference only — it will be considered when admin assigns tutors to groups.</p>
            {tutors.length === 0 ? (
              <div style={{ textAlign:'center', padding:32, color:'#6B7280' }}>No tutors available yet</div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {tutors.map(t => (
                  <button key={t.id} onClick={() => setPref(t.id)} disabled={saving} style={{
                    display:'flex', alignItems:'center', gap:14, padding:'12px 16px',
                    background: prefTutor === t.id ? '#00B4D820' : '#F9FAFB',
                    border: prefTutor === t.id ? '2px solid #00B4D8' : '1px solid #E5E7EB',
                    borderRadius:12, cursor:'pointer', textAlign:'left',
                  }}>
                    <div style={{ width:44, height:44, borderRadius:'50%', background:'#00B4D820', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, overflow:'hidden' }}>
                      {t.photo_url ? <img src={t.photo_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : <span style={{ fontSize:18, fontWeight:700, color:'#00B4D8' }}>{t.full_name?.charAt(0)}</span>}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:15, fontWeight:700, color:'#1F2937' }}>{t.full_name} {prefTutor === t.id && '✓'}</div>
                      {t.bio && <div style={{ fontSize:12, color:'#6B7280', marginTop:2, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{t.bio}</div>}
                      {t.specializations?.length > 0 && <div style={{ marginTop:4, display:'flex', gap:4, flexWrap:'wrap' }}>{t.specializations.slice(0,3).map(s => <span key={s} style={{ fontSize:10, background:'#F3F4F6', padding:'2px 6px', borderRadius:4, color:'#6B7280' }}>{s}</span>)}</div>}
                    </div>
                    {t.rating > 0 && <div style={{ fontSize:12, color:'#F59E0B', flexShrink:0 }}>⭐ {t.rating.toFixed(1)}</div>}
                  </button>
                ))}
              </div>
            )}
            <div style={{ marginTop:16, display:'flex', gap:10, justifyContent:'flex-end' }}>
              {prefTutor && <button onClick={() => setPref(null)} style={{ padding:'8px 16px', border:'1px solid #E5E7EB', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13, color:'#6B7280' }}>Clear preference</button>}
              <button onClick={() => setShowModal(false)} style={{ padding:'8px 20px', border:'1px solid #E5E7EB', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:14 }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function StudentDashboard() {
  const { t, lang, setLanguage } = useTranslation();
  const [dash, setDash] = useState(null);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('grammar');
  const [homework, setHomework] = useState([]);
  const [homeworkPending, setHomeworkPending] = useState(0);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [payProofFile, setPayProofFile] = useState(null);
  const [payUploading, setPayUploading] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [curriculumProgress, setCurriculumProgress] = useState(null);
  const navigate = useNavigate();
  const user = getUser();
  const isCandidate = !!user?.is_candidate;
  const NAV_ITEMS = isCandidate
    ? [['Dashboard','/dashboard'],['Mock Tests','/my-mock-tests'],['Assessment','/assessment']]
    : [['Dashboard','/dashboard'],['My Sessions','/my-sessions'],['Homework','/homework'],['Vocabulary','/vocabulary'],['My ID','/my-id'],['Certificates','/my-certificates'],['Level Exam','/level-exam'],['My Course','/my-course'],['Mock Tests','/my-mock-tests'],['Payments','/my-payments'],['Assessment','/assessment']];

  useEffect(() => {
    api.get('/api/student/dashboard')
      .then(r => setDash(r.data))
      .catch(() => setDash({}))
      .finally(() => setLoading(false));
    api.get('/api/student/upcoming-sessions?days=90')
      .then(r => setUpcomingSessions(r.data.sessions || []))
      .catch(() => {});
    api.get('/api/student/homework').then(r => {
      setHomework(r.data.homework || []);
      setHomeworkPending(r.data.pending_count || 0);
    }).catch(() => {});
    api.get('/api/student/payment-info').then(r => {
      setPaymentInfo(r.data);
      // If routed here to enrol (e.g. from the assessment CTA), scroll to the
      // Course Payment section once it's rendered.
      if (window.location.hash === '#course-payment') {
        setTimeout(() => {
          const el = document.getElementById('course-payment');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    }).catch(() => {});
    api.get('/api/student/curriculum-progress').then(r => setCurriculumProgress(r.data)).catch(() => {});
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? t('dash_good_morning') : hour < 17 ? t('dash_good_afternoon') : t('dash_good_evening');
  const progress = dash?.progress_percent ?? 0;
  const streak = dash?.streak_days ?? 0;
  const xp = dash?.xp_this_week ?? 0;
  // A roadmap/level only exists after the placement assessment. Until then, don't fake one.
  const assessed = !!(dash?.student?.cefr_level || dash?.student?.assessment_completed || dash?.student?.placement_done);
  const level = dash?.student?.cefr_level || 'B1';
  const currentIdx = CEFR_ORDER.indexOf(level);
  const nextLevel = CEFR_ORDER[currentIdx + 1] || null;
  const ROADMAP = CEFR_ORDER.map((lvl, idx) => ({
    level: lvl,
    done: idx < currentIdx,
    current: idx === currentIdx,
  }));
  const vocabCount = dash?.vocabulary_count ?? 0;

  return (
    <div style={{ background:C.bg, minHeight:'100vh' }}>
      {/* Sticky Header */}
      <header style={{ background:C.navy, color:'#fff', padding:'0 20px', height:64, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ fontWeight:800, fontSize:20 }}>SpeakIQ</div>

        {/* Desktop nav */}
        <nav style={{ display:'flex', gap:20 }} className="student-desktop-nav">
          {NAV_ITEMS.map(([label, path]) => (
            <Link key={path} to={path} style={{ color:'rgba(255,255,255,0.8)', textDecoration:'none', fontSize:13, fontWeight:500 }}>{label}</Link>
          ))}
        </nav>

        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {/* Language switcher */}
          <div style={{ position:'relative' }}>
            <button onClick={() => setShowLangMenu(m => !m)} style={{ display:'flex', alignItems:'center', gap:6, background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.25)', borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:12, fontWeight:600, color:'#fff' }}>
              <GlobeAltIcon style={{ width:14, height:14 }} />
              {lang === 'ru' ? '🇷🇺' : lang === 'uz' ? '🇺🇿' : '🇬🇧'}
            </button>
            {showLangMenu && (
              <div style={{ position:'absolute', top:'calc(100% + 6px)', right:0, background:'#fff', border:'1px solid #E5E7EB', borderRadius:10, boxShadow:'0 4px 16px rgba(0,0,0,0.12)', zIndex:300, minWidth:140, overflow:'hidden' }}>
                {[{code:'en',flag:'🇬🇧',label:'English'},{code:'ru',flag:'🇷🇺',label:'Русский'},{code:'uz',flag:'🇺🇿',label:"O'zbekcha"}].map(({code,flag,label}) => (
                  <button key={code} onClick={() => { setLanguage(code); setShowLangMenu(false); }} style={{ display:'block', width:'100%', textAlign:'left', padding:'10px 16px', background: lang===code ? '#F0F7FA' : 'transparent', border:'none', cursor:'pointer', fontSize:14, fontWeight: lang===code ? 700 : 400, color: lang===code ? '#00B4D8' : '#1F2937', borderBottom:'1px solid #E5E7EB' }}>
                    {flag} {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Logout - desktop only */}
          <button onClick={() => { localStorage.clear(); navigate('/login'); }} className="student-desktop-nav" style={{ background:'rgba(255,255,255,0.1)', border:'none', color:'#fff', padding:'7px 14px', borderRadius:8, cursor:'pointer', fontSize:13 }}>
            Logout
          </button>

          {/* Hamburger - mobile only */}
          <button onClick={() => setMobileNavOpen(o => !o)} className="mobile-menu-btn" style={{ background:'none', border:'none', cursor:'pointer', padding:4, color:'#fff', display:'none' }}>
            {mobileNavOpen ? <XMarkIcon style={{ width:24, height:24 }} /> : <Bars3Icon style={{ width:24, height:24 }} />}
          </button>
        </div>
      </header>

      {/* Mobile nav dropdown */}
      {mobileNavOpen && (
        <div style={{ background:C.navy, borderBottom:'1px solid rgba(255,255,255,0.1)', padding:'8px 0', position:'sticky', top:64, zIndex:99 }}>
          {NAV_ITEMS.map(([label, path]) => (
            <Link key={path} to={path} onClick={() => setMobileNavOpen(false)}
              style={{ display:'block', color:'rgba(255,255,255,0.85)', textDecoration:'none', fontSize:14, fontWeight:500, padding:'10px 20px', borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
              {label}
            </Link>
          ))}
          <button onClick={() => { localStorage.clear(); navigate('/login'); }} style={{ display:'block', width:'100%', textAlign:'left', background:'none', border:'none', color:'rgba(255,100,100,0.9)', fontSize:14, fontWeight:500, padding:'10px 20px', cursor:'pointer' }}>
            Logout
          </button>
        </div>
      )}

      <div style={{ maxWidth:1200, margin:'0 auto', padding:'clamp(16px, 4vw, 32px)' }}>
        {upcomingSessions.some(s => s.date === new Date().toISOString().slice(0,10)) && dash?.student?.package === 'physical' && <LocationBanner />}
        {/* Session card logic based on package type */}
        {dash && (() => {
          const pkg = dash?.student?.package;
          const AI_ONLY = ['ai_basic','ai_ielts','ai_business'];
          const GROUP_PKGS = ['online_group','physical','starter_group','standard','premium','ielts_intensive','corporate'];
          if (AI_ONLY.includes(pkg)) return null;
          if (GROUP_PKGS.includes(pkg)) return <TutorPreferenceCard dash={dash} sessions={upcomingSessions} />;
          return <StudentSessionCard />;
        })()}

              {/* Assessment Banner — shown until student completes the placement assessment */}
              {dash && dash.student && !dash.student.assessment_completed && !dash.student.placement_done && (
                <div style={{ background:'linear-gradient(135deg, #0D1B2A, #1a3a5c)', borderRadius:14, padding:24, marginBottom:24, display:'flex', alignItems:'center', gap:20, flexWrap:'wrap' }}>
                  <div style={{ fontSize:40 }}>🎯</div>
                  <div style={{ flex:1, minWidth:200 }}>
                    <div style={{ color:'#fff', fontWeight:800, fontSize:18, marginBottom:6 }}>Complete Your Free Assessment</div>
                    <div style={{ color:'rgba(255,255,255,0.75)', fontSize:14, lineHeight:1.5 }}>Take our short placement test to find your English level and get a personalised study roadmap.</div>
                  </div>
                  <button onClick={() => navigate('/assessment')} style={{ background:'#00B4D8', color:'#fff', border:'none', borderRadius:10, padding:'12px 28px', fontWeight:700, fontSize:15, cursor:'pointer', whiteSpace:'nowrap' }}>
                    Start Assessment →
                  </button>
                </div>
              )}

              {/* Payment Rejection Banner */}
              {paymentInfo && !paymentInfo.course_unlocked && paymentInfo.payment_rejection_reason && paymentInfo.payment_status === 'pending' && (
                <div style={{ background:'#FEF2F2', border:'2px solid #FCA5A5', borderRadius:12, padding:20, marginBottom:16, display:'flex', alignItems:'flex-start', gap:12 }}>
                  <span style={{ fontSize:26, flexShrink:0 }}>⚠️</span>
                  <div>
                    <div style={{ fontWeight:700, fontSize:15, color:'#B91C1C', marginBottom:4 }}>Your payment proof was rejected</div>
                    <div style={{ fontSize:13, color:'#7F1D1D', marginBottom:8 }}>
                      <strong>Reason:</strong> {paymentInfo.payment_rejection_reason}
                    </div>
                    <div style={{ fontSize:13, color:'#991B1B' }}>Please upload a new, correct proof of payment below.</div>
                  </div>
                </div>
              )}

              {/* Payment Banner */}
              {paymentInfo && !paymentInfo.course_unlocked && (
                <div id="course-payment" style={{ background: paymentInfo.payment_status==='submitted' ? '#FEF9C3' : '#EFF6FF', border: paymentInfo.payment_status==='submitted' ? '1px solid #FDE047' : '1px solid #BFDBFE', borderRadius:12, padding:20, marginBottom:20 }}>
                  {paymentInfo.payment_status === 'submitted' ? (
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                      <span style={{ fontSize:28 }}>⏳</span>
                      <div>
                        <div style={{ fontWeight:700, fontSize:15, color:'#92400E' }}>{t('pay_submitted')}</div>
                        <div style={{ fontSize:13, color:'#78350F', marginTop:4 }}>Your payment proof has been received. We will confirm shortly.</div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontWeight:700, fontSize:15, color:'#0D1B2A', marginBottom:12 }}>{t('pay_title')}</div>
                      {paymentInfo.bank_details && paymentInfo.bank_details.local_payment && (
                        <div style={{ background:'#ECFDF5', border:'2px solid #10B981', borderRadius:10, padding:14, marginBottom:14 }}>
                          <div style={{ fontWeight:800, fontSize:14, color:'#065F46', marginBottom:6 }}>💳 Pay by local card — fastest</div>
                          <div style={{ fontSize:14, color:'#0D1B2A', fontWeight:600, lineHeight:1.8 }}>{paymentInfo.bank_details.local_payment}</div>
                        </div>
                      )}
                      {paymentInfo.bank_details && paymentInfo.bank_details.bank_name && (
                        <div style={{ background:'#fff', borderRadius:8, padding:14, marginBottom:14, fontSize:13, lineHeight:1.7 }}>
                          {paymentInfo.bank_details.bank_name && <div><b>{t('pay_bank_name')}:</b> {paymentInfo.bank_details.bank_name}</div>}
                          {paymentInfo.bank_details.account_holder && <div><b>{t('pay_account_holder')}:</b> {paymentInfo.bank_details.account_holder}</div>}
                          {paymentInfo.bank_details.account_number && <div><b>{t('pay_account_number')}:</b> {paymentInfo.bank_details.account_number}</div>}
                          {paymentInfo.bank_details.sort_code && <div><b>{t('pay_sort_code')}:</b> {paymentInfo.bank_details.sort_code}</div>}
                          {paymentInfo.bank_details.swift_bic && <div><b>{t('pay_swift')}:</b> {paymentInfo.bank_details.swift_bic}</div>}
                          {paymentInfo.bank_details.payment_notes && <div style={{ marginTop:6, color:'#6B7280' }}>{paymentInfo.bank_details.payment_notes}</div>}
                        </div>
                      )}
                      <div style={{ fontSize:13, fontWeight:600, marginBottom:6, color:'#0D1B2A' }}>{t('pay_upload_proof')}</div>
                      <input type="file" accept="image/*,application/pdf" onChange={e => setPayProofFile(e.target.files[0])} style={{ fontSize:13, marginBottom:8, display:'block' }} />
                      {payProofFile && (
                        <button onClick={async () => {
                          setPayUploading(true);
                          const fd = new FormData();
                          fd.append('file', payProofFile);
                          try {
                            const r = await api.post('/api/student/upload-payment-proof', fd, { headers: {'Content-Type':'multipart/form-data'} });
                            setPaymentInfo(p => ({ ...p, payment_status:'submitted', payment_proof_url: r.data.proof_url }));
                            setPayProofFile(null);
                          } catch(e) {} finally { setPayUploading(false); }
                        }} disabled={payUploading} style={{ background:'#00B4D8', color:'#fff', border:'none', borderRadius:8, padding:'8px 20px', fontWeight:700, cursor:'pointer', fontSize:13 }}>
                          {payUploading ? t('pay_uploading') : t('pay_upload_done')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
              {paymentInfo && paymentInfo.course_unlocked && (
                <div style={{ background:'#F0FDF4', border:'1px solid #86EFAC', borderRadius:12, padding:16, marginBottom:20, display:'flex', alignItems:'center', gap:12 }}>
                  <span style={{ fontSize:24 }}>✅</span>
                  <div style={{ fontWeight:700, color:'#166534' }}>{t('pay_confirmed')}</div>
                </div>
              )}
        {/* Hero Card */}
        <motion.div
          initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.4 }}
          style={{ background:`linear-gradient(135deg, ${C.navy} 0%, #1e3a5f 100%)`, borderRadius:16, padding:32, marginBottom:24, color:'#fff', position:'relative', overflow:'hidden' }}
        >
          <div style={{ position:'absolute', top:-40, right:-40, width:200, height:200, borderRadius:'50%', background:'rgba(0,180,216,0.1)' }} />
          <div style={{ position:'absolute', bottom:-60, right:80, width:160, height:160, borderRadius:'50%', background:'rgba(0,180,216,0.08)' }} />
          <h2 style={{ margin:'0 0 8px', fontSize:26, fontWeight:800 }}>{greeting}, {user?.first_name || 'Learner'}! 🎓</h2>
          <p style={{ margin:'0 0 24px', opacity:0.75, fontSize:15 }}>Your next AI practice session is ready. Your tutor can see your progress before every class.</p>
          <div style={{ display:'flex', gap:20, flexWrap:'wrap' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:22 }}>🎯</span>
              <div>
                <div style={{ fontSize:12, opacity:0.6 }}>Today's goal</div>
                <div style={{ fontWeight:700, fontSize:16 }}>3 lessons</div>
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:22 }}>🔥</span>
              <div>
                <div style={{ fontSize:12, opacity:0.6 }}>Streak</div>
                <div style={{ fontWeight:700, fontSize:16 }}>{streak} days</div>
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:22 }}>⚡</span>
              <div>
                <div style={{ fontSize:12, opacity:0.6 }}>XP this week</div>
                <div style={{ fontWeight:700, fontSize:16 }}>{xp} XP</div>
              </div>
            </div>
            <div style={{ flex:1, minWidth:200 }}>
              {assessed ? (<>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                {nextLevel ? <span style={{ fontSize:12, opacity:0.6 }}>Progress to next level ({nextLevel})</span> : <span style={{ fontSize:12, opacity:0.6 }}>Top level reached!</span>}
                <span style={{ fontSize:12, fontWeight:700 }}>{progress}%</span>
              </div>
              <div style={{ height:8, background:'rgba(255,255,255,0.15)', borderRadius:4 }}>
                <motion.div
                  initial={{ width:0 }} animate={{ width:`${progress}%` }} transition={{ duration:1, delay:0.5 }}
                  style={{ height:8, background:C.teal, borderRadius:4 }}
                />
              </div>
              </>) : (
                <span style={{ fontSize:12, opacity:0.7 }}>Take your level assessment to see your progress.</span>
              )}
            </div>
          </div>
        </motion.div>

        {/* 3-column middle — 1 col on mobile */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:16, marginBottom:24 }}>
          {/* Today's AI Practice Sessions */}
          <div className="card" style={{ padding:24 }}>
            <h3 style={{ margin:'0 0 8px', fontSize:15, fontWeight:700 }}>Today's AI Practice Sessions</h3>
            {paymentInfo && !paymentInfo.course_unlocked ? (
              <div style={{ textAlign:'center', padding:'20px 0' }}>
                <div style={{ fontSize:36, marginBottom:8 }}>🔒</div>
                <p style={{ fontSize:13, color:C.grey, margin:'0 0 12px' }}>Your course is locked. Complete payment to start lessons.</p>
                <button onClick={() => navigate('/my-course')}
                  style={{ background:C.teal, color:'#fff', border:'none', borderRadius:8, padding:'10px 20px', fontWeight:700, fontSize:13, cursor:'pointer' }}>
                  View Payment Details
                </button>
              </div>
            ) : (
              <>
                <p style={{ margin:'0 0 16px', fontSize:13, color:C.grey }}>Ready to practise</p>
                <div style={{ marginBottom:16 }}>
                  <label style={{ fontSize:13, fontWeight:600, color:C.grey, display:'block', marginBottom:8 }}>Lesson type:</label>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                    {LESSON_TYPES.map(t => (
                      <button key={t.id} onClick={() => setSelectedType(t.id)} style={{
                        padding:'6px 12px', borderRadius:20, fontSize:12, fontWeight:600, cursor:'pointer',
                        background: selectedType===t.id ? t.color : '#F3F4F6',
                        color: selectedType===t.id ? '#fff' : C.grey,
                        border: 'none', transition:'all 0.15s',
                      }}>
                        {t.icon} {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <Link to={`/lesson/${selectedType}`} style={{ textDecoration:'none' }}>
                  <motion.button whileHover={{ scale:1.02 }} whileTap={{ scale:0.98 }}
                    style={{ width:'100%', padding:'14px', background:C.teal, color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:15, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                    <PlayIcon style={{ width:18, height:18 }} /> Start Next Lesson
                  </motion.button>
                </Link>
              </>
            )}
          </div>

          {/* My Progress */}
          <div className="card" style={{ padding:24 }}>
            <h3 style={{ margin:'0 0 20px', fontSize:15, fontWeight:700 }}>{t('dash_my_progress')}</h3>
            <div style={{ textAlign:'center', marginBottom:20 }}>
              <div style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:72, height:72, borderRadius:'50%', background:C.teal+'18', marginBottom:8 }}>
                {assessed ? <CefrBadge level={level} /> : <span style={{ fontSize:28 }}>❓</span>}
              </div>
              <div style={{ fontSize:13, color:C.grey }}>{assessed ? 'Current Level' : 'Not assessed yet'}</div>
            </div>
            {assessed && (
            <div style={{ marginBottom:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, marginBottom:6 }}>
                {nextLevel ? <span style={{ color:C.grey }}>To next level ({nextLevel})</span> : <span style={{ color:C.grey }}>Top level reached!</span>}
                <span style={{ fontWeight:700 }}>{progress}%</span>
              </div>
              <div style={{ height:8, background:'#E5E7EB', borderRadius:4 }}>
                <div style={{ height:8, background:C.success, borderRadius:4, width:`${progress}%` }} />
              </div>
            </div>
            )}
            <div style={{ display:'flex', gap:16 }}>
              <div style={{ flex:1, textAlign:'center', padding:12, background:'#F9FAFB', borderRadius:8 }}>
                <div style={{ fontSize:22, fontWeight:800, color:C.teal }}>{vocabCount}</div>
                <div style={{ fontSize:11, color:C.grey }}>Words learned</div>
              </div>
              <div style={{ flex:1, textAlign:'center', padding:12, background:'#F9FAFB', borderRadius:8 }}>
                <div style={{ fontSize:22, fontWeight:800, color:C.success }}>{dash?.lessons_this_week ?? 0}</div>
                <div style={{ fontSize:11, color:C.grey }}>Lessons/week</div>
              </div>
            </div>
          </div>

          {/* Curriculum Coverage */}
          {curriculumProgress && (
            <div className="card" style={{ padding:24 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <h3 style={{ margin:0, fontSize:15, fontWeight:700 }}>Curriculum Coverage</h3>
                <span style={{ background:'#EFF6FF', color:'#1D4ED8', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:700 }}>{curriculumProgress.level}</span>
              </div>

              {/* Overall progress */}
              <div style={{ marginBottom:16 }}>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#6B7280', marginBottom:6 }}>
                  <span>{curriculumProgress.covered} of {curriculumProgress.total_materials} topics covered</span>
                  <span style={{ fontWeight:700, color:'#0D1B2A' }}>{curriculumProgress.coverage_percent}%</span>
                </div>
                <div style={{ height:8, background:'#E5E7EB', borderRadius:4 }}>
                  <div style={{ height:8, background:'#10B981', borderRadius:4, width:`${curriculumProgress.coverage_percent}%`, transition:'width 0.5s' }} />
                </div>
              </div>

              {/* By skill */}
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {Object.entries(curriculumProgress.by_skill || {}).map(([skill, data]) => {
                  const SKILL_COLORS = {grammar:'#6366F1',vocabulary:'#059669',reading:'#F59E0B',writing:'#EF4444',speaking:'#00B4D8'};
                  const pct = data.total ? Math.round(data.covered / data.total * 100) : 0;
                  return (
                    <div key={skill}>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:3 }}>
                        <span style={{ fontWeight:600, color:'#0D1B2A', textTransform:'capitalize' }}>{skill}</span>
                        <span style={{ color:'#6B7280' }}>{data.covered}/{data.total}</span>
                      </div>
                      <div style={{ height:6, background:'#F3F4F6', borderRadius:3 }}>
                        <div style={{ height:6, background:SKILL_COLORS[skill]||'#10B981', borderRadius:3, width:`${pct}%`, transition:'width 0.5s' }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Recently covered */}
              {(curriculumProgress.covered_materials || []).length > 0 && (
                <div style={{ marginTop:16, paddingTop:12, borderTop:'1px solid #E5E7EB' }}>
                  <div style={{ fontSize:12, fontWeight:600, color:'#6B7280', marginBottom:8 }}>Recently covered</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                    {curriculumProgress.covered_materials.slice(0,5).map(m => (
                      <span key={m.id} style={{ fontSize:11, background:'#F0FDF4', color:'#065F46', padding:'3px 10px', borderRadius:20, border:'1px solid #BBF7D0' }}>
                        ✓ {m.title.length > 20 ? m.title.slice(0,20)+'...' : m.title}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* My Roadmap */}
          <div className="card" style={{ padding:24 }}>
            <h3 style={{ margin:'0 0 20px', fontSize:15, fontWeight:700 }}>My Roadmap</h3>
            {!assessed ? (
              <div style={{ textAlign:'center', padding:'20px 10px' }}>
                <div style={{ fontSize:34, marginBottom:10 }}>🧭</div>
                <div style={{ fontSize:14, color:C.grey, marginBottom:14, lineHeight:1.6 }}>Take the level assessment to discover your CEFR level and unlock your personalised roadmap.</div>
                <button onClick={() => navigate('/assessment')} style={{ padding:'10px 20px', borderRadius:10, background:C.teal, color:'#fff', border:'none', fontWeight:700, cursor:'pointer', fontSize:13 }}>Take Assessment →</button>
              </div>
            ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {ROADMAP.map((node, i) => (
                <div key={node.level} style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
                    <div style={{
                      width:32, height:32, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
                      background: node.done ? C.success : node.current ? C.teal : '#E5E7EB',
                      color: node.done || node.current ? '#fff' : C.grey,
                      fontWeight:700, fontSize:12, flexShrink:0,
                    }}>
                      {node.done ? '✓' : node.level}
                    </div>
                    {i < ROADMAP.length-1 && <div style={{ width:2, height:12, background: node.done ? C.success : '#E5E7EB', marginTop:2 }} />}
                  </div>
                  <div>
                    <span style={{ fontSize:14, fontWeight: node.current ? 700 : 400, color: node.current ? C.dark : node.done ? C.grey : '#9CA3AF' }}>
                      {node.level} {node.current ? '← You are here' : ''}
                    </span>
                    {node.current && <div className="pulse-anim" style={{ display:'inline-block', width:6, height:6, background:C.teal, borderRadius:'50%', marginLeft:6 }} />}
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>
        </div>

        {/* Bottom 3 panels — 1 col on mobile */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:16 }}>
          {/* Homework Card */}
          <div className="card" style={{ padding:24, cursor:'pointer' }} onClick={() => navigate('/homework')}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
              <h3 style={{ margin:0, fontSize:15, fontWeight:700 }}>My Homework</h3>
              {homeworkPending > 0 && (
                <span style={{ background:'#EF4444', color:'#fff', borderRadius:20, padding:'2px 10px', fontSize:12, fontWeight:700 }}>
                  {homeworkPending}
                </span>
              )}
            </div>
            {homework.length === 0 ? (
              <div style={{ textAlign:'center', padding:'20px 0', color:'#6B7280' }}>
                <ClipboardDocumentListIcon style={{ width:36, height:36, margin:'0 auto 8px', color:'#D1D5DB', display:'block' }} />
                <p style={{ margin:0, fontSize:13 }}>Complete an AI practice session to receive homework</p>
              </div>
            ) : (
              <div>
                {homework.slice(0,3).map((hw) => {
                  const isPending = hw.status === 'pending' || hw.status === 'late';
                  const isDone = hw.status === 'submitted' || hw.status === 'reviewed';
                  return (
                    <div key={hw.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid #E5E7EB' }}>
                      <span style={{ fontSize:16 }}>{isDone ? '✅' : hw.status === 'late' ? '⚠️' : '📋'}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{hw.title}</div>
                        {hw.submission && <div style={{ fontSize:11, color:'#10B981' }}>{hw.submission.percentage}% scored</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <button
              onClick={() => navigate('/homework')}
              style={{ width:'100%', marginTop:16, padding:'10px', background:'#0D1B2A', color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer' }}>
              View All Homework →
            </button>
          </div>

          {/* Certificates */}
          <div className="card" style={{ padding:24 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <h3 style={{ margin:0, fontSize:15, fontWeight:700 }}>Recent Certificates</h3>
              <Link to="/my-certificates" style={{ color:C.teal, fontSize:13, fontWeight:600, textDecoration:'none' }}>View All →</Link>
            </div>
            {(dash?.certificates || []).length === 0 ? (
              <div style={{ textAlign:'center', padding:24, color:C.grey }}>
                <TrophyIcon style={{ width:40, height:40, margin:'0 auto 8px', color:'#D1D5DB' }} />
                <p style={{ margin:0, fontSize:14 }}>Complete your level exam to earn your first certificate!</p>
                <Link to="/level-exam" style={{ color:C.teal, fontWeight:600, fontSize:14, textDecoration:'none', display:'block', marginTop:8 }}>Take Level Exam →</Link>
              </div>
            ) : (
              dash.certificates.map((c, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 0', borderBottom:`1px solid ${C.border}` }}>
                  <div style={{ width:44, height:44, borderRadius:8, background:C.success+'18', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <TrophyIcon style={{ width:22, height:22, color:C.success }} />
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600, fontSize:14 }}>{c.level} Certificate</div>
                    <div style={{ fontSize:12, color:C.grey }}>{new Date(c.issued_at).toLocaleDateString('en-GB')}</div>
                  </div>
                  <Link to="/my-certificates" style={{ color:C.teal, fontSize:13, fontWeight:600, textDecoration:'none' }}>View</Link>
                </div>
              ))
            )}
          </div>


          {/* Book a Tutor */}
          <div className="card" style={{ padding:24 }}>
            <h3 style={{ margin:'0 0 4px', fontSize:15, fontWeight:700 }}>Book Your Class</h3>
            <p style={{ color:C.grey, fontSize:13, margin:'0 0 16px' }}>Book a class at your centre — group classes, IELTS or Business English</p>
            <button onClick={() => navigate('/book-tutor')}
              style={{ width:'100%', padding:'14px 0', background:C.teal, color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor:'pointer', marginBottom:10 }}>
              📅 Book Your Class
            </button>
            <button onClick={() => navigate('/programmes')}
              style={{ width:'100%', padding:'12px 0', background:'#F3F4F6', color:C.dark, border:`1px solid ${C.border}`, borderRadius:10, fontWeight:600, fontSize:13, cursor:'pointer' }}>
              📋 View Learning Programmes
            </button>
            {dash?.student?.package && (
              <div style={{ marginTop:12, background:'#D1FAE5', borderRadius:8, padding:'8px 12px', fontSize:12, color:'#065F46', fontWeight:600 }}>
                Current plan: {dash?.student?.package}
              </div>
            )}
            {!dash?.student?.package && (
              <div style={{ marginTop:12, background:'#FEF9C3', borderRadius:8, padding:'8px 12px', fontSize:12, color:'#854D0E' }}>
                Enrol on a programme to access tutor classes
              </div>
            )}
          </div>
        </div>

        {/* Learning Mode — only physical package students can switch between school / home */}
        {dash?.student?.package === 'physical' && <div className="card" style={{ marginTop: 20, padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.navy }}>Learning Mode</h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: C.grey }}>Switch between remote learning and attending the physical school.</p>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            {[
              { mode: 'remote', label: '🌐 Remote', color: C.teal },
              { mode: 'physical', label: '🏫 Physical', color: C.success },
            ].map(opt => (
              <button
                key={opt.mode}
                onClick={() => {
                  api.patch('/api/student/learning-mode', { learning_mode: opt.mode })
                    .then(() => setDash(d => d ? { ...d, learning_mode: opt.mode } : d))
                    .catch(() => {});
                }}
                style={{
                  padding: '10px 20px', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer',
                  border: '2px solid ' + opt.color,
                  background: dash && (dash?.student?.learning_mode || 'remote') === opt.mode ? opt.color : '#fff',
                  color: dash && (dash?.student?.learning_mode || 'remote') === opt.mode ? '#fff' : opt.color,
                  transition: 'all 0.2s',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>}
          <StudentMyTests />
      </div>
    </div>
  );
}
